import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Container, Draggable } from "react-smooth-dnd"
import styled from "@emotion/styled"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import { useSnackbar } from "notistack"
import {
  getProgramBySubCategoryId,
  exchangeProgramIndex,
} from "services/program"
import { verifyResponse } from "utils/httpRequest"
import { exchangeArrayItem } from "utils/data"
import { StyledCategoryProgramItem } from "./Category.style"

const StyledCategoryReorderProgramDialog = styled(Dialog)`
  .MuiPaper-root {
    width: min(980px, calc(100vw - 30px));
    min-height: 560px;
    border-radius: 18px;
    border: 1px solid var(--cms-border);
    background: var(--cms-dialog-bg);
    box-shadow: 0 24px 52px rgba(15, 23, 42, 0.2);
  }

  .MuiDialogTitle-root {
    padding: 18px 24px 6px;
  }

  .dialog-title {
    margin: 0;
    font-size: 20px;
    font-weight: 800;
    color: var(--cms-text-primary);
    letter-spacing: -0.01em;
  }

  .dialog-subtitle {
    margin: 6px 0 0;
    font-size: 13px;
    color: var(--cms-text-secondary);
    font-weight: 500;
  }

  .MuiDialogContent-root {
    padding: 10px 20px 12px;
  }

  .reorder-list {
    border: 1px dashed var(--cms-border);
    border-radius: 14px;
    padding: 12px;
    background: var(--cms-surface-muted);
    min-height: 320px;
  }

  .empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 140px;
    border-radius: 12px;
    border: 1px dashed var(--cms-border);
    color: var(--cms-text-secondary);
    font-size: 14px;
    font-weight: 600;
  }

  .MuiDialogActions-root {
    padding: 10px 22px 20px;
    gap: 10px;
  }

  .dialog-save-btn {
    text-transform: none;
    font-weight: 700;
    border-radius: 10px;
    padding: 8px 16px;
    box-shadow: 0 12px 24px rgba(29, 78, 216, 0.22);
  }

  .dialog-close-btn {
    text-transform: none;
    font-weight: 700;
    color: var(--cms-text-secondary);
  }

  .smooth-dnd-draggable-wrapper {
    padding: 0 5px;
  }
`

const CategoryReorderProgramDlg = ({ access }) => {
  const { enqueueSnackbar } = useSnackbar()

  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [category, setCategory] = useState(null)
  const [programs, setPrograms] = useState([])
  const [isChanged, setIsChanged] = useState(false)

  const openDlg = (currentSubCategory) => {
    setCategory(currentSubCategory)
    setIsChanged(false)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const loadPrograms = async () => {
    try {
      if (category) {
        setLoading(true)
        const response = await getProgramBySubCategoryId(category.id)
        verifyResponse(response)

        if (response.rtnCode === 200) {
          setPrograms(response.data.list || [])
        } else {
          enqueueSnackbar("Fail to list category programs", {
            variant: "warning",
          })
        }
      }
    } catch (e) {
      enqueueSnackbar("Fail to list category programs", {
        variant: "warning",
      })
    } finally {
      setLoading(false)
    }
  }

  const updateOrder = async () => {
    try {
      setLoading(true)
      const response = await exchangeProgramIndex({
        subCategoryId: category.id,
        updateIndexArray: programs.map((program, index) => {
          return {
            id: program.id,
            index: index,
          }
        }),
      })

      if (response.rtnCode !== 200) {
        enqueueSnackbar("Fail to the program order", {
          variant: "warning",
        })
      } else {
        setIsChanged(false)
        enqueueSnackbar("Update program order success", {
          variant: "success",
        })
      }
    } catch (e) {
      enqueueSnackbar("Fail to the program order", {
        variant: "warning",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (category) {
      loadPrograms()
    }
  }, [category])

  // render program list item
  const renderProgramItem = (program) => {
    const thumbnailUrl =
      program?.thumbResourceDto?.urlDto?.imgUrl ||
      program?.thumbResourceDto?.urlDto?.originalUrl ||
      ""

    return (
      <StyledCategoryProgramItem key={`category-program-item-${program.id}`}>
        <div className="thumbnail">
          <img src={thumbnailUrl} alt="thumbnail" />
        </div>
        <div className="info">
          <div className="identity">Program ID: {program.id} </div>
          <div className="title">Title: {program.title}</div>
          <div className="desc">{program.description}</div>
        </div>
      </StyledCategoryProgramItem>
    )
  }

  const dlg = (
    <StyledCategoryReorderProgramDialog open={open} onClose={handleClose}>
      <DialogTitle>
        <h3 className="dialog-title">{`Reorder Programs: ${
          category?.name || ""
        }`}</h3>
        <p className="dialog-subtitle">
          Drag and drop to set program display order for this subcategory.
        </p>
      </DialogTitle>
      <DialogContent>
        <div className="reorder-list">
          {programs?.length === 0 && (
            <div className="empty-state">No programs found for this subcategory.</div>
          )}
          {access.listProgramBySubCategoryAccess ? (
            <Container
              onDrop={async (e) => {
                setIsChanged(true)
                const updateIndexArray = exchangeArrayItem({
                  addedIndex: e.addedIndex,
                  removedIndex: e.removedIndex,
                  indexString: "programIndex",
                  list: programs,
                })
                setPrograms(updateIndexArray)
              }}
            >
              {programs &&
                programs.map((program) => (
                  <Draggable key={program.id}>
                    {renderProgramItem(program)}
                  </Draggable>
                ))}
            </Container>
          ) : (
            programs &&
            programs.map((program) => (
              <Draggable key={program.id}>
                {renderProgramItem(program)}
              </Draggable>
            ))
          )}
        </div>
      </DialogContent>

      <DialogActions>
        {access.listProgramBySubCategoryAccess && (
          <Button
            variant="contained"
            color="primary"
            onClick={updateOrder}
            disabled={!isChanged}
            className="dialog-save-btn"
          >
            Save
          </Button>
        )}
        <Button className="dialog-close-btn" onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </StyledCategoryReorderProgramDialog>
  )

  return [openDlg, dlg, loading]
}

CategoryReorderProgramDlg.propTypes = {
  onSubmit: PropTypes.func,
}

CategoryReorderProgramDlg.defaultProps = {
  onSubmit: null,
}

export default CategoryReorderProgramDlg
